import React, { useState } from 'react';
import { FileText, MessageSquare, BookOpen } from 'lucide-react';
import Button from '../../../components/ui/Button';

const NotesSection = ({ 
  notes, 
  termsAndConditions, 
  onNotesChange, 
  onTermsChange 
}) => {
  const [showTemplates, setShowTemplates] = useState(false);

  // Predefined note templates
  const noteTemplates = [
    {
      id: 1,
      title: 'Standard Service Note',
      content: 'Thank you for your business! Please remit payment within the agreed terms. Contact us if you have any questions about this invoice.'
    },
    {
      id: 2,
      title: 'Project Completion',
      content: 'This invoice covers the completion of the agreed project scope. All deliverables have been provided as specified in our agreement.'
    },
    {
      id: 3,
      title: 'Recurring Service',
      content: 'This invoice is for recurring services as per our ongoing service agreement. Next billing cycle will occur on the scheduled date.'
    },
    {
      id: 4,
      title: 'Custom Work',
      content: 'This invoice includes custom work performed outside of standard services. Additional details and specifications are available upon request.'
    }
  ];

  // Predefined terms and conditions templates
  const termsTemplates = [
    {
      id: 1,
      title: 'Standard Terms',
      content: `1. Payment is due within the specified payment terms.
2. Late payments may be subject to interest charges.
3. All work performed is guaranteed for 30 days from completion.
4. Any disputes must be raised within 7 days of invoice receipt.
5. Services are provided as agreed in the contract or work order.`
    },
    {
      id: 2,
      title: 'Software Development',
      content: `1. Code ownership transfers upon full payment.
2. Support period begins after project acceptance.
3. Source code will be delivered after final payment.
4. Bug fixes are included for 30 days post-delivery.
5. Additional features require separate agreement.`
    },
    {
      id: 3,
      title: 'Consulting Services',
      content: `1. All recommendations are based on information provided.
2. Implementation of recommendations is client's responsibility.
3. Confidentiality agreements remain in effect.
4. Additional hours beyond scope will be billed separately.
5. Travel expenses are billed at cost if applicable.`
    }
  ];

  const applyTemplate = (template, type) => {
    if (type === 'notes') {
      onNotesChange(template?.content);
    } else {
      onTermsChange(template?.content);
    }
    setShowTemplates(false);
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Notes & Terms</h2>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="BookOpen"
          iconPosition="left"
          onClick={() => setShowTemplates(!showTemplates)}
        >
          Templates
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notes Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <label className="text-sm font-medium text-foreground">
              Notes (Visible to Customer)
            </label>
          </div>
          
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(e?.target?.value)}
            placeholder="Add any additional notes for the customer..."
            className="w-full h-32 p-3 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            maxLength={500}
          />
          
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>These notes will appear on the customer's invoice</span>
            <span>{notes?.length || 0}/500</span>
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            <label className="text-sm font-medium text-foreground">
              Terms & Conditions
            </label>
          </div>
          
          <textarea
            value={termsAndConditions}
            onChange={(e) => onTermsChange(e?.target?.value)}
            placeholder="Enter terms and conditions..."
            className="w-full h-32 p-3 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            maxLength={1000}
          />
          
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>Legal terms and conditions for this invoice</span>
            <span>{termsAndConditions?.length || 0}/1000</span>
          </div>
        </div>
      </div>
      {/* Template Panel */}
      {showTemplates && (
        <div className="mt-6 pt-6 border-t">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Note Templates */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">Note Templates</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {noteTemplates?.map(template => (
                  <div
                    key={template?.id}
                    className="p-3 border rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => applyTemplate(template, 'notes')}
                  >
                    <p className="font-medium text-sm">{template?.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {template?.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Terms Templates */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">Terms Templates</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {termsTemplates?.map(template => (
                  <div
                    key={template?.id}
                    className="p-3 border rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => applyTemplate(template, 'terms')}
                  >
                    <p className="font-medium text-sm">{template?.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-3">
                      {template?.content?.split('\n')?.[0]}...
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Quick Actions */}
      <div className="mt-4 pt-4 border-t">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Quick actions:</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => onNotesChange('Thank you for your business!')}
          >
            Add Thank You
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => onTermsChange('Payment due within 30 days of invoice date.')}
          >
            Standard Terms
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => {
              onNotesChange('');
              onTermsChange('');
            }}
          >
            Clear All
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotesSection;